import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SafeHtml } from '../../components/SafeHtml';
import { useFieldguideStore } from '../../stores/root_store';

interface FieldGuideEntryEditProps {
  entrySlug?: string;  // If editing existing entry
  sectionSlug?: string;  // If creating new entry, which section
  guideType: string;
}

export const FieldGuideEntryEdit = observer(
  ({ entrySlug, sectionSlug, guideType }: FieldGuideEntryEditProps) => {
    const navigate = useNavigate();
    const fieldguideStore = useFieldguideStore();

    const isNew = !entrySlug;
    const entry = fieldguideStore.currentEntry;

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [contentType, setContentType] = useState<string>('text/markdown');
    const [selectedSectionSlug, setSelectedSectionSlug] = useState(
      sectionSlug || ''
    );
    const [previewTab, setPreviewTab] = useState(0); // 0 = edit, 1 = preview

    // Load entry data when editing
    useEffect(() => {
      if (!isNew && entry) {
        setTitle(entry.title || '');
        setContent(entry.content || '');
        setContentType(entry.content_type || 'text/html');
      }
    }, [isNew, entry]);

    // Set initial section from param
    useEffect(() => {
      if (sectionSlug) {
        setSelectedSectionSlug(sectionSlug);
      }
    }, [sectionSlug]);

    const handleSave = async () => {
      if (!title.trim() || !content.trim()) return;

      if (isNew) {
        if (!selectedSectionSlug) return;
        const newEntry = await fieldguideStore.createEntry({
          section_slug: selectedSectionSlug,
          title: title.trim(),
          content: content.trim(),
          content_type: contentType,
        });
        if (newEntry) {
          navigate(`/assistant/fieldguide/${guideType}/${newEntry.slug}`);
        }
      } else if (entrySlug) {
        // Find which section this entry belongs to
        let entrySectionSlug = '';
        for (const section of fieldguideStore.sections) {
          const entries = fieldguideStore.getEntriesForSection(section.slug);
          if (entries.some((e) => e.slug === entrySlug)) {
            entrySectionSlug = section.slug;
            break;
          }
        }
        const updated = await fieldguideStore.updateEntry(
          entrySlug,
          entrySectionSlug,
          {
            title: title.trim(),
            content: content.trim(),
            content_type: contentType,
          }
        );
        if (updated) {
          navigate(`/assistant/fieldguide/${guideType}/${updated.slug}`);
        }
      }
    };

    const handleDelete = async () => {
      if (!entrySlug) return;
      if (!confirm('Delete this entry?')) return;

      let entrySectionSlug = '';
      for (const section of fieldguideStore.sections) {
        const entries = fieldguideStore.getEntriesForSection(section.slug);
        if (entries.some((e) => e.slug === entrySlug)) {
          entrySectionSlug = section.slug;
          break;
        }
      }

      const deleted = await fieldguideStore.deleteEntry(
        entrySlug,
        entrySectionSlug
      );
      if (deleted) {
        navigate(`/assistant/fieldguide/${guideType}`);
      }
    };

    const isMarkdown =
      contentType === 'text/markdown' || contentType === 'markdown';

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {isNew ? 'New Entry' : 'Edit Entry'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isNew && (
            <FormControl fullWidth size="small">
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSectionSlug}
                onChange={(e) => setSelectedSectionSlug(e.target.value)}
                label="Section"
              >
                {fieldguideStore.sections.map((s) => (
                  <MenuItem key={s.uuid} value={s.slug}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={fieldguideStore.saving}
            required
            size="small"
          />

          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Content Type</InputLabel>
            <Select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              label="Content Type"
            >
              <MenuItem value="text/markdown">Markdown</MenuItem>
              <MenuItem value="text/html">HTML</MenuItem>
            </Select>
          </FormControl>

          {/* Edit/Preview Tabs */}
          <Tabs
            value={previewTab}
            onChange={(_, v) => setPreviewTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Edit" />
            <Tab label="Preview" />
          </Tabs>

          {previewTab === 0 ? (
            <TextField
              label="Content"
              fullWidth
              multiline
              minRows={12}
              maxRows={30}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={fieldguideStore.saving}
              required
              placeholder={
                isMarkdown
                  ? '# Heading\n\nWrite your content in **markdown**...'
                  : '<h1>Heading</h1>\n<p>Write your content in HTML...</p>'
              }
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                minHeight: 200,
                '& img': { maxWidth: '100%', height: 'auto' },
                '& a': { color: 'primary.main' },
                '& pre': {
                  bgcolor: 'action.hover',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                },
                '& code': {
                  bgcolor: 'action.hover',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                },
                '& pre code': {
                  bgcolor: 'transparent',
                  px: 0,
                },
              }}
            >
              {content ? (
                isMarkdown ? (
                  <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
                ) : (
                  <SafeHtml content={content} />
                )
              ) : (
                <Typography color="text.secondary">
                  Nothing to preview.
                </Typography>
              )}
            </Box>
          )}

          {fieldguideStore.saveError && (
            <Alert severity="error">{fieldguideStore.saveError}</Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                fieldguideStore.saving ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={
                !title.trim() ||
                !content.trim() ||
                (isNew && !selectedSectionSlug) ||
                fieldguideStore.saving
              }
            >
              {isNew ? 'Create Entry' : 'Save Changes'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate(`/assistant/fieldguide/${guideType}`)}
              disabled={fieldguideStore.saving}
            >
              Cancel
            </Button>

            {!isNew && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={fieldguideStore.saving}
                sx={{ ml: 'auto' }}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);
